// ─── File-wide constants ─────────────────────────────────────────────

const CODE_BLOCK_RE = /(```[\s\S]*?```|`[^`]+`)/g;

const MATH_ENVS = new Set([
	'align',
	'equation',
	'gather',
	'multline',
	'eqnarray',
	'displaymath',
	'cases',
	'pmatrix',
	'bmatrix',
	'vmatrix',
	'Vmatrix',
	'matrix',
]);

const MATH_ENV_PATTERN = [...MATH_ENVS].join('|');

/** KaTeX commands that require displayMode: true. */
const DISPLAY_ONLY_RE = /\\tag\b/;

// TODO: env regex construction repeats with subtle variants across MATH_REGION_RE,
// normalizeMathRegions' envCollapseRe, and shieldLatexForRemark's bareEnvRe
// (with/without backref, with/without `(?<!$)` shielding). A small
// `buildEnvRegex(pattern, opts)` helper would centralize this; revisit if a
// 4th call site appears.

/** Single source of truth for "what counts as math" when prose-only rules must skip math. */
const MATH_REGION_RE = new RegExp(
	[
		'\\$\\$[\\s\\S]*?\\$\\$',
		'(?<!\\\\)\\\\\\[[\\s\\S]*?(?<!\\\\)\\\\\\]',
		'(?<!\\\\)\\\\\\([\\s\\S]*?(?<!\\\\)\\\\\\)',
		'\\$(?:[^$\\\\]|\\\\.)+\\$',
		`\\\\begin\\{(?:${MATH_ENV_PATTERN})\\*?\\}[\\s\\S]*?\\\\end\\{(?:${MATH_ENV_PATTERN})\\*?\\}`,
	].join('|'),
	'g',
);

// ─── Generic helpers ─────────────────────────────────────────────────

/** Transforms only the prose segments, leaving code spans/fences untouched. */
function mapNonCode(content: string, fn: (part: string) => string): string {
	const parts = content.split(CODE_BLOCK_RE);
	return parts.map((part, i) => (i % 2 === 0 ? fn(part) : part)).join('');
}

/** Like mapNonCode, but also skips math regions — for rules that should only touch prose. */
function mapProse(content: string, fn: (part: string) => string): string {
	return mapNonCode(content, (codeFreePart) => {
		let result = '';
		let lastIndex = 0;
		// matchAll returns a fresh iterator each call; no shared regex cursor to leak across invocations.
		for (const match of codeFreePart.matchAll(MATH_REGION_RE)) {
			result += fn(codeFreePart.slice(lastIndex, match.index));
			result += match[0];
			lastIndex = match.index + match[0].length;
		}
		result += fn(codeFreePart.slice(lastIndex));
		return result;
	});
}

/** Flattens a math block onto one line so it survives markdown table cells. */
function collapseMath(s: string): string {
	return s.replace(/(?<!\\)%.*$/gm, '').replace(/\n/g, ' ');
}

/** LLMs sometimes emit `\*` (markdown escape) inside math; KaTeX has no `\*`. */
function unescapeMathStar(s: string): string {
	return s.replace(/(?<!\\)\\\*/g, '*');
}

/**
 * Walks regex matches that come in open/close pairs (e.g. \left/\right,
 * \begin/\end) and returns the stack of unmatched openers.
 */
function collectUnmatched(
	text: string,
	re: RegExp,
	openKey: string,
): string[][] {
	const stack: string[][] = [];
	re.lastIndex = 0;
	let match = re.exec(text);
	while (match) {
		if (match[1] === openKey) stack.push(match.slice(1));
		else stack.pop();
		match = re.exec(text);
	}
	return stack;
}

// ─── Unclosed-math detection (private) ───────────────────────────────

type MathState = 'none' | 'inline$' | 'display$$' | 'bracket' | 'paren' | 'env';

interface UnclosedMath {
	state: MathState;
	openPos: number;
	envName?: string;
}

/**
 * Character-level scanner that tracks math-mode open/close across the
 * full message. Returns the outermost unclosed delimiter, or null.
 */
function detectUnclosedMath(content: string): UnclosedMath | null {
	if (!content) return null;

	const fences = content.match(/```/g);
	if (fences && fences.length % 2 !== 0) return null;

	const parts = content.split(CODE_BLOCK_RE);

	let state: MathState = 'none';
	let openPos = -1;
	let envName: string | undefined;
	let pos = 0;

	parts.forEach((t, i) => {
		if (i % 2 === 1) {
			pos += t.length;
			return;
		}

		let j = 0;
		while (j < t.length) {
			const c = t[j];
			const c2 = t[j + 1];
			let advance = 1;

			if (c === '\\' && c2 === '\\') {
				advance = 2;
			} else if (state === 'none') {
				if (c === '\\' && c2 === '$') {
					advance = 2;
				} else if (c === '\\' && c2 === '[') {
					state = 'bracket';
					openPos = pos + j;
					advance = 2;
				} else if (c === '\\' && c2 === '(') {
					state = 'paren';
					openPos = pos + j;
					advance = 2;
				} else if (c === '$' && c2 === '$') {
					state = 'display$$';
					openPos = pos + j;
					advance = 2;
				} else if (c === '$') {
					state = 'inline$';
					openPos = pos + j;
				} else if (c === '\\') {
					const m = t.slice(j).match(/^\\begin\{(\w+\*?)\}/);
					if (m && MATH_ENVS.has(m[1].replace(/\*$/, ''))) {
						state = 'env';
						openPos = pos + j;
						[envName] = m.slice(1);
						advance = m[0].length;
					}
				}
			} else if (c === '\\' && c2 === '$') {
				advance = 2;
			} else if (state === 'display$$' && c === '$' && c2 === '$') {
				state = 'none';
				openPos = -1;
				advance = 2;
			} else if (state === 'inline$' && c === '$') {
				state = 'none';
				openPos = -1;
			} else if (state === 'bracket' && c === '\\' && c2 === ']') {
				state = 'none';
				openPos = -1;
				advance = 2;
			} else if (state === 'paren' && c === '\\' && c2 === ')') {
				state = 'none';
				openPos = -1;
				advance = 2;
			} else if (state === 'env' && c === '\\') {
				const m = t.slice(j).match(/^\\end\{\w+\*?\}/);
				if (m) {
					state = 'none';
					openPos = -1;
					envName = undefined;
					advance = m[0].length;
				}
			}

			j += advance;
		}
		pos += t.length;
	});

	if (state !== 'none' && openPos >= 0) return { state, openPos, envName };
	return null;
}

// ─── Public: completion repair ───────────────────────────────────────

/**
 * Streaming guard: strips everything from the last unclosed math
 * delimiter onward so remark-math never sees unmatched syntax.
 */
export function trimIncompleteLatex(content: string): string {
	const unclosed = detectUnclosedMath(content);
	if (unclosed) return content.slice(0, unclosed.openPos);
	return content;
}

/** Closing delimiters for `\left` openers; consumed only by closeIncompleteLatex. */
const DELIM_PAIRS: Record<string, string> = {
	'(': ')',
	'[': ']',
	'\\{': '\\}',
	'|': '|',
	'<': '>',
	'\\langle': '\\rangle',
	'\\lfloor': '\\rfloor',
	'\\lceil': '\\rceil',
};

/**
 * Completed-message repair: appends the correct closing syntax for
 * unclosed braces, \left/\right, \begin/\end, and the outer delimiter
 * so KaTeX can render truncated LLM output.
 */
export function closeIncompleteLatex(content: string): string {
	const unclosed = detectUnclosedMath(content);
	if (!unclosed) return content;

	let result = content;
	const tail = content.slice(unclosed.openPos);

	// Braces
	let braceDepth = 0;
	[...tail].forEach((ch, i) => {
		if (i > 0 && tail[i - 1] === '\\') return;
		if (ch === '{') braceDepth += 1;
		else if (ch === '}') braceDepth -= 1;
	});
	if (braceDepth > 0) result += '}'.repeat(braceDepth);

	// \left / \right pairs
	const leftDelims = collectUnmatched(
		tail,
		/\\(left|right)(\\[a-zA-Z]+|\\.|[\s\S])/g,
		'left',
	);
	for (let i = leftDelims.length - 1; i >= 0; i -= 1) {
		result += `\\right${DELIM_PAIRS[leftDelims[i][1]] || '.'}`;
	}

	// \begin / \end pairs
	const openEnvs = collectUnmatched(
		tail,
		/\\(begin|end)\{(\w+\*?)\}/g,
		'begin',
	);
	for (let i = openEnvs.length - 1; i >= 0; i -= 1) {
		result += `\\end{${openEnvs[i][1]}}`;
	}

	// Outer math delimiter (env state already closed by \begin/\end scan)
	const closers: Record<MathState, string> = {
		none: '',
		inline$: '$',
		display$$: '$$',
		bracket: '\\]',
		paren: '\\)',
		env: '',
	};
	return result + closers[unclosed.state];
}

// ─── Public: pipeline normalization ──────────────────────────────────

// TODO: opaque. The three replaces normalize various LLM-mangled forms of
// matrix env delimiters (dropped/duplicated backslashes around `{matrix}`,
// `begin\end{matrix}` artifacts) back to clean `\begin{matrix}` / `\end{matrix}`.
// Document the exact mangling patterns being recovered, or replace with a
// targeted parser if a regression surfaces.
function repairMatrixDelimiters(s: string): string {
	return s
		.replace(
			/\\{0,8}\{\s*([a-zA-Z]{1,32}matrix)\s*\\{0,8}\}/g,
			(_m, t) => `\\end{${t}}`,
		)
		.replace(/\\?begin\s*\\end\{([a-zA-Z]+matrix)\}/g, '\\begin{$1}')
		.replace(/\\?end\s*\\end\{([a-zA-Z]+matrix)\}/g, '\\end{$1}');
}

/** Per-region cleanup inside math: unescape `\*`, collapse multiline blocks for table cells. */
function normalizeMathRegions(content: string): string {
	const envBegin = `\\\\begin\\{(${MATH_ENV_PATTERN})\\*?\\}`;
	const envEnd = `\\\\end\\{\\1\\*?\\}`;
	const envCollapseRe = new RegExp(`${envBegin}([\\s\\S]*?)${envEnd}`, 'g');

	return mapNonCode(content, (part) =>
		part
			.replace(
				/(?<!\\)\\\[([\s\S]*?)(?<!\\)\\\]/g,
				(_m, inner) => `\\[${unescapeMathStar(collapseMath(inner))}\\]`,
			)
			.replace(
				/(?<!\\)\\\(([\s\S]*?)(?<!\\)\\\)/g,
				(_m, inner) => `\\(${unescapeMathStar(inner)}\\)`,
			)
			.replace(
				/\$\$\s*([\s\S]*?)\s*\$\$/g,
				(_m, inner) => `$$${unescapeMathStar(collapseMath(inner))}$$`,
			)
			.replace(envCollapseRe, (match) =>
				unescapeMathStar(collapseMath(match)),
			)
			.replace(
				/(?<!\$)\$(?!\$)((?:[^$\\]|\\.)+)\$(?!\$)/g,
				(_m, inner) => {
					const cleaned = unescapeMathStar(inner);
					return DISPLAY_ONLY_RE.test(cleaned)
						? `\n$$\n${cleaned}\n$$\n`
						: `$${cleaned}$`;
				},
			),
	);
}

/** LaTeX document-structure → markdown. Prose-only; never fires inside math. */
function rewriteDocStructure(content: string): string {
	return mapProse(content, (part) =>
		part
			.replace(/\\section\*?\{([^}]*)\}/g, '\n## $1\n')
			.replace(/\\subsection\*?\{([^}]*)\}/g, '\n### $1\n')
			.replace(/\\subsubsection\*?\{([^}]*)\}/g, '\n#### $1\n')
			.replace(/\\begin\{(itemize|enumerate)\}/g, '')
			.replace(/\\end\{(itemize|enumerate)\}/g, '')
			.replace(/\\item\s*/g, '- '),
	);
}

/** Keep markdown's HTML parser away from prose `<` / `>`; never fires inside math. */
function escapeProseHtml(content: string): string {
	return mapProse(content, (part) =>
		part
			.replace(/<(?=\s)/g, '&lt;')
			.replace(/(?<![a-zA-Z0-9/"'])>(?=\s)/gm, '&gt;'),
	);
}

/**
 * Normalises malformed LLM LaTeX before the markdown parser sees it: repairs
 * matrix delimiters, collapses multiline math onto single lines (so they
 * survive table cells), unescapes markdown-style `\*` inside math, rewrites
 * document-structure commands (\section, \item, etc.) to Markdown, and
 * escapes prose `<` / `>` adjacent to whitespace.
 */
export function preprocessLaTeX(content: string): string {
	if (typeof content !== 'string') return content;
	let clean = content;
	clean = repairMatrixDelimiters(clean);
	clean = normalizeMathRegions(clean);
	clean = rewriteDocStructure(clean);
	clean = escapeProseHtml(clean);
	return clean;
}

/** Subset of MATH_ENVS that establish their own math mode (no wrapping $$ needed). */
const TOP_LEVEL_ENV_PATTERN =
	'align|equation|gather|multline|eqnarray|displaymath';

/**
 * Chat pipeline only: converts paired \[...\] → $$...$$ and \(...\) → $...$
 * for remark-math, which only understands dollar-sign delimiters.
 * Lone (unpaired) delimiters are left inert to avoid broken math blocks.
 */
export function shieldLatexForRemark(content: string): string {
	if (!content) return content;

	const envBegin = `(?<!\\$)\\\\begin\\{(${TOP_LEVEL_ENV_PATTERN})\\*?\\}`;
	const envEnd = `\\\\end\\{\\1\\*?\\}(?!\\$)`;
	const bareEnvRe = new RegExp(`${envBegin}[\\s\\S]*?${envEnd}`, 'g');

	return mapNonCode(content, (part) =>
		part
			.replace(
				/(?<!\\)\\\[([\s\S]*?)(?<!\\)\\\]/g,
				(_m, inner) => `\n$$\n${unescapeMathStar(inner.trim())}\n$$\n`,
			)
			.replace(/(?<!\\)\\\(([\s\S]*?)(?<!\\)\\\)/g, (_m, inner) => {
				const cleaned = unescapeMathStar(inner);
				return DISPLAY_ONLY_RE.test(cleaned)
					? `\n$$\n${cleaned.trim()}\n$$\n`
					: `$${cleaned}$`;
			})
			.replace(
				bareEnvRe,
				(match) => `\n$$\n${unescapeMathStar(match.trim())}\n$$\n`,
			),
	);
}
